// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { EducationalTooltip } from '@/components/educational-tooltip';
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useRef } from 'react';

type AssetPresenceReportKind = 'inventory' | 'field' | 'transit';

interface Props {
  title: string;
  reportKind: AssetPresenceReportKind;
}

const REPORT_COVERAGE: Record<AssetPresenceReportKind, string> = {
  inventory:
    'Shows which tracked assets were at your inventory locations (such as warehouses and other sites marked as inventory) on each day in the date range you choose.',
  field:
    'Shows which tracked assets were at field sites (such as customer sites or other locations that are not treated as inventory) on each day in the date range you choose.',
  transit:
    'Shows which tracked assets were in transit between locations on each day in the date range you choose, based on recorded ship and delivery dates.',
};

/**
 * Report title with an info control that explains what the presence grid includes.
 */
export function AssetPresenceReportEducationalTitle(props: Props) {
  const infoTooltip = useDisclosure();
  const infoButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h5">{props.title}</Typography>
        <IconButton
          ref={infoButtonRef}
          size="small"
          onClick={infoTooltip.open}
          aria-label={`Learn about the ${props.title}`}
        >
          <InfoOutlined fontSize="small" />
        </IconButton>
      </Stack>
      <EducationalTooltip
        title={props.title}
        isOpen={infoTooltip.isOpen}
        onClose={infoTooltip.close}
        anchorElement={infoButtonRef.current}
        placement="bottom-start"
      >
        <Stack spacing={2}>
          <Typography variant="body2">
            {REPORT_COVERAGE[props.reportKind]}
          </Typography>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              How each day is marked
            </Typography>
            <Typography variant="body2">
              A checked day means the asset fit this report for any part of that
              calendar day, using the ship and delivery times recorded in
              Hardfin. It is not limited to where the asset ended the day. (An
              asset can appear on more than one report on the same day if it
              satisfies each report's rules. For example, if an asset leaves a
              customer site and is received at a warehouse on the same day.)
            </Typography>
          </Box>
        </Stack>
      </EducationalTooltip>
    </>
  );
}
